import { DataTable } from '@/components/table/DataTable';
import { TanstackSortableColumnHeader } from '@/components/table/SortColumnHeader';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FlexCol } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Multiselect,
  type MultiselectOption,
} from '@/components/ui/multiselect';
import {
  useAdminConsultantOrgsService,
  useAdminCreateConsultantOrgService,
  useAdminDeleteConsultantOrgService,
  useAdminUpdateConsultantOrgService,
  useAdminUploadConsultantOrgLogoService,
  useBackOfficeUsersService,
  useBackOfficeWorkspacesService,
} from '@/services/backoffice';
import type { ConsultantOrganizationItem } from '@ditto/shared';
import { CONSULTANT_PATH } from '@/paths';
import { cn } from '@/utils/classUtils';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { getSortedRowModel } from '@tanstack/react-table';
import {
  ExternalLink,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { type FC, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export const ConsultantsPage: FC = () => {
  const { consultantOrgs, isLoading } = useAdminConsultantOrgsService();
  const { deleteOrg } = useAdminDeleteConsultantOrgService();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<ConsultantOrganizationItem | null>(
    null
  );
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = useMemo<ColumnDef<ConsultantOrganizationItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Name
          </TanstackSortableColumnHeader>
        ),
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
      },
      {
        accessorKey: 'memberCount',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Members
          </TanstackSortableColumnHeader>
        ),
      },
      {
        accessorKey: 'workspaceCount',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Workspaces
          </TanstackSortableColumnHeader>
        ),
      },
      {
        id: 'actions',
        header: () => <div className="text-right">Actions</div>,
        meta: { headerClassName: 'w-[80px]', cellClassName: 'text-right' },
        cell: ({ row }) => {
          const org = row.original;
          return (
            <div
              className="flex items-center justify-end gap-1"
              onClick={e => e.stopPropagation()}
            >
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => navigate(`${CONSULTANT_PATH}/${org.id}`)}
                title="Open consultant portal"
              >
                <ExternalLink className="size-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                    <MoreHorizontal className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => navigate(`${CONSULTANT_PATH}/${org.id}`)}
                  >
                    <ExternalLink className="size-4" />
                    View portal
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setEditItem(org)}>
                    <Pencil className="size-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-red-600"
                    onClick={() => {
                      deleteOrg(org.id, {
                        onSuccess: () => toast.success(`Deleted ${org.name}`),
                        onError: (e: Error) => toast.error(e.message),
                      });
                    }}
                  >
                    <Trash2 className="size-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [deleteOrg, navigate, setEditItem]
  );

  if (isLoading) return <div className="p-4 text-neutral-500">Loading...</div>;

  return (
    <FlexCol gap="6" w="full" h="full" p="6" className="max-w-[1600px]">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Consultant Organizations</h2>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4 mr-1" />
            Add Organization
          </Button>
        </div>

        <DataTable
          columns={columns}
          data={consultantOrgs}
          hidePagination
          onRowClick={org => setEditItem(org)}
          options={{
            getRowId: row => row.id,
            onSortingChange: setSorting,
            getSortedRowModel: getSortedRowModel(),
            enableMultiSort: false,
            state: { sorting },
          }}
        />

        <CreateOrgModal open={createOpen} onOpenChange={setCreateOpen} />

        {editItem && (
          <EditOrgModal
            open={!!editItem}
            onOpenChange={open => !open && setEditItem(null)}
            org={editItem}
          />
        )}
      </div>
    </FlexCol>
  );
};

// ── Create Modal ──

const CreateOrgModal: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ open, onOpenChange }) => {
  const [name, setName] = useState('');
  const { createOrg, isPending } = useAdminCreateConsultantOrgService();

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    createOrg(
      { name: name.trim() },
      {
        onSuccess: () => {
          toast.success('Organization created');
          setName('');
          onOpenChange(false);
        },
        onError: (e: Error) => toast.error(e.message),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Organization</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Acme Consulting"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Logo Drop Zone ──

const LogoDropZone: FC<{
  currentLogoUrl: string | null;
  isUploading: boolean;
  onFile: (file: File) => void;
}> = ({ currentLogoUrl, isUploading, onFile }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    if (file.type.startsWith('image/')) onFile(file);
  };

  return (
    <div
      className={cn(
        'flex items-center gap-4 rounded-lg border-2 border-dashed p-4 transition-colors',
        dragging ? 'border-blue-400 bg-blue-50/50' : 'border-neutral-200'
      )}
      onDragOver={e => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => {
        e.preventDefault();
        setDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
      }}
    >
      {currentLogoUrl && (
        <img
          src={currentLogoUrl}
          alt=""
          className="h-12 w-12 rounded border object-contain shrink-0"
        />
      )}
      <div className="flex flex-col gap-1 flex-1 min-w-0">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 self-start"
        >
          <Upload className="size-3.5" />
          {isUploading
            ? 'Uploading...'
            : currentLogoUrl
              ? 'Change logo'
              : 'Upload logo'}
        </button>
        <span className="text-xs text-neutral-400">
          or drag & drop an image
        </span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
};

// ── Edit Modal ──

const EditOrgModal: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  org: ConsultantOrganizationItem;
}> = ({ open, onOpenChange, org }) => {
  const [name, setName] = useState(org.name);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>(
    org.memberIds ?? []
  );
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>(
    org.workspaceIds ?? []
  );
  const { updateOrg, isPending } = useAdminUpdateConsultantOrgService();
  const { uploadLogo, isPending: isUploading } =
    useAdminUploadConsultantOrgLogoService();
  const { users } = useBackOfficeUsersService();
  const { workspaces } = useBackOfficeWorkspacesService();

  // Re-sync when org changes (e.g. navigating between orgs)
  useMemo(() => {
    setName(org.name);
    setSelectedMemberIds(org.memberIds ?? []);
    setSelectedWorkspaceIds(org.workspaceIds ?? []);
  }, [org]);

  const userOptions: MultiselectOption[] = useMemo(
    () =>
      users
        .filter(u => u.type === 'consultant')
        .map(u => ({
          label:
            [u.firstName, u.lastName].filter(Boolean).join(' ') ||
            u.email ||
            u.id,
          value: u.id,
        })),
    [users]
  );

  const workspaceOptions: MultiselectOption[] = useMemo(
    () =>
      workspaces.map(w => ({
        label: `${w.name} (${w.uname})`,
        value: w.id,
      })),
    [workspaces]
  );

  const selectedMemberDetails = useMemo(
    () => users.filter(u => selectedMemberIds.includes(u.id)),
    [users, selectedMemberIds]
  );

  const selectedWorkspaceDetails = useMemo(
    () => workspaces.filter(w => selectedWorkspaceIds.includes(w.id)),
    [workspaces, selectedWorkspaceIds]
  );

  const membersTrigger = (
    <div className="min-h-[40px] w-full border rounded-md px-3 py-2 bg-background">
      {selectedMemberDetails.length === 0 ? (
        <span className="text-muted-foreground">Select members...</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {selectedMemberDetails.map(u => (
            <div
              key={u.id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-sm"
            >
              <span>
                {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}
              </span>
              <button
                type="button"
                onPointerDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedMemberIds(prev => prev.filter(id => id !== u.id));
                }}
                className="hover:bg-secondary-foreground/20 rounded-sm p-0.5"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const workspacesTrigger = (
    <div className="min-h-[40px] w-full border rounded-md px-3 py-2 bg-background">
      {selectedWorkspaceDetails.length === 0 ? (
        <span className="text-muted-foreground">Select workspaces...</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {selectedWorkspaceDetails.map(w => (
            <div
              key={w.id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-sm"
            >
              <span className="font-mono">{w.uname}</span>
              <button
                type="button"
                onPointerDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedWorkspaceIds(prev =>
                    prev.filter(id => id !== w.id)
                  );
                }}
                className="hover:bg-secondary-foreground/20 rounded-sm p-0.5"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    updateOrg(
      {
        id: org.id,
        body: {
          name: name.trim(),
          memberIds: selectedMemberIds,
          workspaceIds: selectedWorkspaceIds,
        },
      },
      {
        onSuccess: () => {
          toast.success('Organization updated');
          onOpenChange(false);
        },
        onError: (e: Error) => toast.error(e.message),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Organization</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Organization name"
            />
          </div>

          <div className="space-y-2">
            <Label>Logo</Label>
            <LogoDropZone
              currentLogoUrl={
                org.logo
                  ? `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/public/consultant-logos/${org.id}`
                  : null
              }
              isUploading={isUploading}
              onFile={file => {
                uploadLogo(
                  { orgId: org.id, file },
                  {
                    onSuccess: () => toast.success('Logo uploaded'),
                    onError: error =>
                      toast.error(
                        error instanceof Error
                          ? error.message
                          : 'Failed to upload logo'
                      ),
                  }
                );
              }}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-base font-semibold">Members</Label>
            <Multiselect
              options={userOptions}
              selected={selectedMemberIds}
              onChange={setSelectedMemberIds}
              placeholder="Select members..."
              showSearch
              showClearAll
              searchPlaceholder="Search users..."
              trigger={membersTrigger}
              contentClassName="max-h-[300px] overflow-y-auto w-full"
            />
            <p className="text-xs text-muted-foreground">
              {selectedMemberIds.length === 0
                ? 'No members'
                : `${selectedMemberIds.length} member(s)`}
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-base font-semibold">Workspaces</Label>
            <Multiselect
              options={workspaceOptions}
              selected={selectedWorkspaceIds}
              onChange={setSelectedWorkspaceIds}
              placeholder="Select workspaces..."
              showSearch
              showClearAll
              searchPlaceholder="Search workspaces..."
              trigger={workspacesTrigger}
              contentClassName="max-h-[300px] overflow-y-auto w-full"
            />
            <p className="text-xs text-muted-foreground">
              {selectedWorkspaceIds.length === 0
                ? 'No workspaces'
                : `${selectedWorkspaceIds.length} workspace(s)`}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
